import axiosInstance from "./axiosInstance";

export interface HourReportingPlacementPeriod {
    year: number;
    periodType: "week";
    periodNumber: number;
    start: string;
    end: string;
}

export interface HourReportingPlacementRow {
    placementId: string;
    workerId: string | null;
    placementNumber: number | null;
    organizationName: string | null;
    clientName: string | null;
    title: string | null;
    functionName: string | null;
    workerName: string;
    workerPhoneNumber: string | null;
    whatsappNumber: string | null;
    startDate: string | null;
    endDate: string | null;
    isActive: boolean;
    declarationId: string | null;
    declarationStatus: string;
    declarationStatusSortOrder: number;
    processStatusWorker: string | null;
    processStatusRelation: string | null;
    declarationPeriod: {
        start: string | null;
        end: string | null;
    };
    editable: boolean;
    hasHoursReported: boolean;
    latestContextError?: string | null;
}

export interface HourReportingPlacementListResponse {
    period: HourReportingPlacementPeriod;
    items: HourReportingPlacementRow[];
}

export interface HourReportingPlacementDetail {
    period: HourReportingPlacementPeriod;
    item: HourReportingPlacementRow;
    declaration: Record<string, any> | null;
    declarationContext: Record<string, any>;
    latestWorkflow: Record<string, any> | null;
    workflowDraftLines: Array<{
        date: string | null;
        startTime: string | null;
        endTime: string | null;
        breakMinutes: number | null;
        workedMinutes: number | null;
        remarks: string | null;
        source: "confirmed" | "extracted" | "canonical";
    }>;
}

export interface WhatsappTemplateParameterExample {
    param_name?: string;
    example?: string;
}

export interface WhatsappTemplateComponent {
    type: string;
    format?: string;
    text?: string;
    example?: {
        body_text?: string[][];
        body_text_named_params?: WhatsappTemplateParameterExample[];
    };
}

export interface WhatsappTemplateSummary {
    id: string;
    name: string;
    language: string;
    status: string;
    category: string;
    parameter_format?: "NAMED" | "POSITIONAL";
    components?: WhatsappTemplateComponent[];
}

export interface WhatsappTemplateListResponse {
    items: WhatsappTemplateSummary[];
    configured: boolean;
    message: string | null;
}

export async function listHourReportingPlacements(params: {
    year?: number;
    periodNumber?: number;
    periodType?: "week";
}): Promise<HourReportingPlacementListResponse> {
    const response = await axiosInstance.get<HourReportingPlacementListResponse>("/timesheets/operator/placements", {
        params,
    });
    return response.data;
}

export async function getHourReportingPlacementDetail(
    placementId: string,
    params: {
        year?: number;
        periodNumber?: number;
        periodType?: "week";
    },
): Promise<HourReportingPlacementDetail> {
    const response = await axiosInstance.get<HourReportingPlacementDetail>(`/timesheets/operator/placements/${placementId}`, {
        params,
    });
    return response.data;
}

export async function listWhatsappTemplates(): Promise<WhatsappTemplateListResponse> {
    const response = await axiosInstance.get<WhatsappTemplateListResponse>("/whatsapp/templates");
    return {
        items: Array.isArray(response.data?.items) ? response.data.items : [],
        configured: Boolean(response.data?.configured),
        message: response.data?.message || null,
    };
}

export async function sendWhatsappTemplateBatch(items: Array<{
    to: string;
    templateName: string;
    languageCode: string;
    parameters?: Array<{ text: string; parameterName?: string | null }>;
}>): Promise<Array<Record<string, any>>> {
    const response = await axiosInstance.post<{ results: Array<Record<string, any>> }>("/whatsapp/templates/send-batch", {
        items,
    });
    return Array.isArray(response.data?.results) ? response.data.results : [];
}
